/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useRef, SetStateAction } from "react";
import { Input, RadioGroup, RadioGroupItem, useToast } from "@coreai/component-library";
import { css, useTheme } from "@emotion/react";
import {
  listRulesets,
  RulesetExtItem,
  RulesetModel,
} from "../../../../services/rulesetService";
import { useLanguage } from "LanguageContext";
import { REACT_APP_NEW_THEME } from "services/config";

interface TextFieldWithRadioDropdownProps {
  setRulesets: (ruleset: RulesetModel | null) => void;
  setMultiRulesets: (multiRulesets: RulesetExtItem[] | null) => void;
  ruleId: string | null;
  setPoliticalRiskAssmnt: (riskAssmnt: string | null) => void;
  politicalRiskId?: string | null;
  fileType?:string | null;
  assetType?: string | null;
}

type GroupedDropdown = {
  [category: string]: RulesetExtItem[];
};

const TextFieldWithRadioDropdown: React.FC<TextFieldWithRadioDropdownProps> = (
  props: TextFieldWithRadioDropdownProps
) => {
  const { t, language } = useLanguage();
  const theme = useTheme();
  const { toast } = useToast();
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedOption, setSelectedOption] = useState<RulesetModel | null>(
    null
  );
  const [rulesets, setRulesets] = useState<RulesetModel[]>([]);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [groupedDropdown, setGroupedDropdown] = useState<GroupedDropdown>({});
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<RulesetExtItem[]>([]);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const listRef = useRef<(HTMLLIElement | null)[]>([]);
  const [selectedPoliticalRisk, setSelectedPoliticalRisk] = useState<RulesetModel | null>(null);


const handleSelectCompliance = (ruleset: RulesetModel) => {
  setSelectedOption(ruleset);
  props.setRulesets(ruleset); // mandatory selection
};

const handleChangePoliticalRisk = (ruleset: RulesetModel) => {
  const newSelection = selectedPoliticalRisk?.id === ruleset.id ? null : ruleset;
  setSelectedPoliticalRisk(newSelection);
  props.setPoliticalRiskAssmnt(newSelection ? newSelection.id : null); // optional
};

  

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    async function fetchData() {
      const rulesetList = await listRulesets();
      if (!rulesetList?.results) return;
      
      setRulesets((prevRulesets) => {
        const newRule = rulesetList?.results?.filter(
          (result: any) =>
            !prevRulesets.some((suggestion) => suggestion.id === result.id)
        );

        return [
          ...prevRulesets,
          ...newRule.map((result: any) => ({
            id: result?.id,
            name: result?.front_name,
            stage: result?.stage,
            category: result?.category,
          })),
        ];
      });
      
      if (window.location.hostname.includes(REACT_APP_NEW_THEME)) {
        const _rulesetList: any = []
        rulesetList?.results?.forEach((ruleset: any) => {
          if (ruleset.worker_id === "publicis-positive-representations") {
            _rulesetList.push({
              id: ruleset.id,
              name: ruleset.name,
              stage: ruleset.stage,
              category: ruleset.category,
              displayName: t(ruleset.front_name) || ''
            });
          }
        });
        setSelectedItems(_rulesetList);
        if(_rulesetList?.[0]?.category) {
          setSelectedCategory(_rulesetList[0].category);
        }
      } else if(rulesetList?.results?.[0]) {
        setSelectedOption({
          id: rulesetList.results[0].id,
          name: rulesetList.results[0].front_name,
          stage: rulesetList.results[0].stage,
          category: rulesetList.results[0].category,
        });
        setSelectedCategory(rulesetList.results[0].category);
      }        
    }

    fetchData();
  }, [t]);

  useEffect(() => {
    if (selectedOption) {
      props.setRulesets(selectedOption);
      props.setMultiRulesets(null)
    } else if (selectedItems.length > 0) {
      props.setMultiRulesets(selectedItems);
      props.setRulesets(null)
    }
  }, [props, selectedOption, selectedItems]);

  useEffect(() => {
    let _ruleIDs = props.ruleId?.split(",").map((rule) => rule.trim());
    let preselectedRules : any= [];
    if(props.ruleId){
      rulesets.filter((rule)=>{
        if (_ruleIDs?.includes(rule.id)) {
          preselectedRules.push({
            id: rule.id,
            name: rule.name,
            stage: rule.stage,
            category: rule.category,
            displayName: t(rule.name)  || ''
          });
        }
      })
      if (preselectedRules.length > 0) {
        setSelectedCategory(preselectedRules[0].category);
        setSelectedItems([...preselectedRules]);
        if (preselectedRules.length === 1) {
          delete preselectedRules[0].displayName;
          setSelectedOption(preselectedRules[0]);
        }
      }
    }
    if(props.politicalRiskId) {
      const politicalRiskRule = rulesets.find((rule) => rule.id === props.politicalRiskId);
      if (politicalRiskRule) {
        setSelectedPoliticalRisk(politicalRiskRule);
        props.setPoliticalRiskAssmnt(politicalRiskRule?.id || null);
      }
    }
  }, [props?.ruleId, rulesets, props?.politicalRiskId]);



  useEffect(() => {
    const groupedDropdown = groupByCategory(rulesets);
    setGroupedDropdown(groupedDropdown);
  }, [rulesets, t]);

  const multiDropdownStyles = css`
    // overflow: hidden;
    // position: absolute;
    // background-color: white;
    // border: 1px solid ${theme.colors.strokeGray};
    // border-radius: 8px;
    width: 100%;
    // z-index: 999;
    display: flex;
    flex-direction: column;
    // box-shadow: 0px 4px 30px rgba(0, 0, 0, 0.15);
    padding: 24px 0px 12px;
  `;

  const titleStyle = css`
    font-family: Volte;
    font-weight: 600;
    font-size: 12px;
    line-height: 150%;
    text-transform: uppercase;
    color: ${theme.colors.feedbackTitleColor};
  `;

  const categoryListItemStyles = css`
    &:not(:last-child) {
      border-bottom: 1px solid #EAEEF0;
      margin-bottom: 8px;
      padding-bottom: 12px;
    }
    padding: 0px 24px;
   `;

  const optionListStyles = css`
    padding: 8px 0px;
    color: ${theme.colors.primary};
    > label {
      display: flex;
      gap: 8px;
      align-items: center;
      line-height: 21px;
      cursor: pointer;
      > p {
        margin-top: 0px;
        width: 80%;
      }
    }
  `;
  const optionListCheckboxStyles = css`
    width: 24px;
    height: 24px;
    cursor: pointer;
    accent-color: ${theme.colors.primary};
  `;

  const refFrameSelectionStyle = css`
    position: absolute;
    overflow: hidden;
    width: 100%;
    z-index: 999;
    background-color: white;
    border: 1px solid ${theme.colors.strokeGray};
    border-radius: 8px;
    box-shadow: 0px 4px 30px rgba(0, 0, 0, 0.15);
  `;

  const usaSelectionStyle = css`
    padding: 12px 16px 16px;
    border-top: 1px solid ${theme.colors.strokeGray};
  `;

  const labelWrapper = css`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
  `;

  const refFrameOption = css`
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  background-color: transparent;
  color: black;
  transition: background-color 0.2s;
  &:hover {
    background-color: #f2f2f2;
  }
`;

const selectedRefFrameOption = css`
  background-color: #f2f2f2;
  &:hover {
    background-color: #f2f2f2;
  }
`;

  const containerStyles = {
    position: "relative",
    width: "100%",
  } as React.CSSProperties;

  const onClick = (option: any) => {
    setSelectedOption(option);
    setShowDropdown(false);
  };

  const groupByCategory = (data: RulesetModel[]): GroupedDropdown => {
    return data.reduce((acc: GroupedDropdown, item) => {
      const category = item?.category || "compliance";
      if (!acc[category]) {
        acc[category] = [];
      }
      // acc[category].push({...item, displayName: `${t(item.name)}${ item.stage ? ` (${t(item.stage)})` : ""}`});
      acc[category].push({...item, displayName: t(item.name) || ''});
      return acc;
    }, {});
  };

  const handleCheckboxChange = (e: any, item: RulesetModel) => {
    // If switching to a new category, reset all selections
    if (e.target.checked) {
      setSelectedOption(null)
      if (item.category !== selectedCategory) {
        setSelectedItems([item]);
        setSelectedCategory(item.category);
      } else {
        setSelectedCategory(item.category);
        // Toggle selection within the same category
        setSelectedItems((prev: RulesetModel[]) => {
          if (prev.some((selectedItem) => selectedItem.id === item.id)) {
            return prev.filter((selectedItem) => selectedItem.id !== item.id);
          } else {
            return [...prev, item];
          }
        });
      }
    } else {
      setSelectedItems((prev: RulesetModel[]) =>
        prev.filter((selectedItem) => selectedItem.id !== item.id)
      );
    }
    
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
      if (!showDropdown) return;
       let keyName = e?.key?.toLocaleLowerCase();
      if (keyName === "arrowdown") {
        e.preventDefault();
         setActiveIndex((prev) => (prev + 1) % rulesets?.length);       
      } else if (keyName === "escape") {
          e.preventDefault();
          setShowDropdown(false);
       } else if (keyName === "arrowup") {
        e.preventDefault();
         setActiveIndex((prev) => (prev - 1 + rulesets?.length) % rulesets?.length);
      } else if (keyName === "enter") {
        if (activeIndex >= 0) {
          handleCheckboxChange(e, rulesets[activeIndex]);
          setShowDropdown(false);
        }
      }
    };
  useEffect(() => {
      if (activeIndex >= 0 && listRef.current[activeIndex]) {
        listRef.current[activeIndex]?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }, [activeIndex]);

    useEffect(() => {
      if (language === "fr" && selectedPoliticalRisk?.id) {
        toast({
          variant: 'error',
          title: t("risk_flagging_us_error_info"),
          });
        // reset selection
        setSelectedPoliticalRisk(null);
        props.setPoliticalRiskAssmnt(null);
      }
    }, [language, props, selectedPoliticalRisk?.id, t, toast]);

      useEffect(() => {
        const handleKeyDown = (event: any) => {
          if (event.key === "Escape") {
            setShowDropdown(false);
            // Close any other popups or reset their states here
          }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => {
          document.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    const groupedRulesets = rulesets.reduce((acc, ruleset) => {
      if (!acc[ruleset.category]) {
        acc[ruleset.category] = [];
      }
      acc[ruleset.category].push(ruleset);
      return acc;
    }, {} as Record<string, typeof rulesets>);

    const truncate = (str: string, length = 30) =>
      str?.length > length ? str.slice(0, length) + "..." : str;

  return (
    <div style={containerStyles} ref={dropdownRef}>
      <Input
        style={{cursor:'pointer'}}
        type="text"
        label={t("analyse_form_label_referenceFrame")}
        readOnly
        onFocus={() => setShowDropdown(true)}
        placeholder={t("analyse_form_placeholder_referenceFrame")}
        value={(() => {
          const hasPoliticalRisk = !!selectedPoliticalRisk?.name;
      
          if (selectedOption) {
            const main = `${t(selectedOption.name)}${
              selectedOption.stage ? ` (${t(selectedOption.stage)})` : ""
            }`;
      
            return [
              hasPoliticalRisk ? truncate(main) : main,
              hasPoliticalRisk && selectedPoliticalRisk ? t(selectedPoliticalRisk.name) : ""
            ]
              .filter(Boolean)
              .join(", ");
          }
      
          const parts = [
            ...(hasPoliticalRisk
              ? selectedItems.map((item) => truncate(item?.displayName ?? ""))
              : selectedItems.map((item) => item?.displayName ?? "")),
            hasPoliticalRisk && selectedPoliticalRisk ? t(selectedPoliticalRisk.name) : ""
          ].filter(Boolean);
      
          return parts.join(", ");
        })()}
        suffixIcon={showDropdown ?"chevron-down":"chevron-up"}
        autoComplete="off"
        name="referenceframe"
        required
      />

      {showDropdown && ( 
        <div css={refFrameSelectionStyle}>
        {window.location.hostname.includes(REACT_APP_NEW_THEME) ? (
        <ul css={multiDropdownStyles}  onKeyDown={handleKeyDown} tabIndex={0}>
          {Object.entries(groupedDropdown).sort(([a], [b]) => b.localeCompare(a)).map(([category, items]) => (
            <li key={category} css={categoryListItemStyles}>
              <div css={titleStyle}>{t(category)}</div>
                {items.map((item) => (
                  <div key={item.id} css={optionListStyles}>
                    <label>
                      <input
                        type="checkbox"
                        checked={selectedItems.some((selectedItem) => selectedItem.id === item.id)}
                        onChange={(e) => handleCheckboxChange(e, item)}
                        css={optionListCheckboxStyles}
                      />
                      <p>{item.displayName}</p>
                    </label>
                  </div>
                ))}
            </li>
          ))}
        </ul>) 
        : 
        Object.entries(groupedRulesets).map(([category, rules]) => { 
          if (language === "fr" && category === "political_risk") {
            return null;
          }
          return(
          <div css={usaSelectionStyle} key={category}>
            <div css={titleStyle}>{category === "political_risk" ? t("risk_flagging_words_political_risk_assmnt") : t("compliance")}</div>
        
                {category === "political_risk" ? (
                  rules.map((ruleset) => (
                    <label key={ruleset.id} css={labelWrapper}>
                    <input
                    type="checkbox"
                    checked={selectedPoliticalRisk?.id === ruleset.id}
                    onChange={() => handleChangePoliticalRisk(ruleset)}
                    css={optionListCheckboxStyles}
                    // disabled={!(props.fileType === null)}
                  />
                    {(props.fileType === null) ? (
                        <span>{t(ruleset.name)}</span>
                    ) : (
                      <span>{t(ruleset.name)}</span>
                    )}
                </label>
              ))
            ) : (
              rules.map((ruleset) => {
                const isSelected = selectedOption?.id === ruleset.id;
                return (
                  <div
                    key={ruleset.id}
                    onClick={() => handleSelectCompliance(ruleset)}
                    css={[refFrameOption, isSelected && selectedRefFrameOption]}
                  >
                    {ruleset.stage
                      ? `${t(ruleset.name)} (${t(ruleset.stage)})`
                      : t(ruleset.name)}
                  </div>
                );
              })
            )}
          </div>
        )})}        
        </div>
      )}
    </div>
  );
};

export default TextFieldWithRadioDropdown;
